/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { Dropdown, IDropdownOption, MessageBar, MessageBarType } from "@fluentui/react"
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react"
import { getTreatyYears } from "../lib/helper"
import { makeApiCall, makeCRMApiCall } from "../services/apiService"

type ParameterType = "Dropdown"

type ParamterResult = string | number | string[] | number[]

export interface IReportParameter {
	Label: string
	AllowMultiple: boolean
	Function: string
	Type: ParameterType
	RenderKeyInTitle?: boolean
}

interface IReportParameterWithResults extends IReportParameter {
	Result: ParamterResult
}

export interface IReportParameters {
	[x: string]: IReportParameter
}

interface IParamterResults {
	ParameterElements: JSX.Element[]
	AllParametersComplete: boolean
	Results: IReportParameterWithResults[]
}

export const ParametersContext = createContext<IParamterResults>({
	ParameterElements: [],
	AllParametersComplete: undefined,
	Results: []
})

export const useParametersContext = () => useContext(ParametersContext)

export type ParametersProviderProps = {
	parameters: IReportParameters
}

export interface DynamicFunction {
	(...args: any[]): any
}

const functionMap: { [name: string]: DynamicFunction } = {}

function getGroupCodesBroker(): Promise<IDropdownOption[]> {
	// eslint-disable-next-line promise/prefer-await-to-then
	return makeApiCall("getGroupsForBroker").then(results => {
		const uniqueGroupCodes = results.reduce((acc: any[], groupCode: any) => {
			if (!acc.some(item => item.key === groupCode.cw_groupcode)) {
				acc.push(groupCode)
			}
			return acc
		}, [])

		return uniqueGroupCodes.map((groupCode: any) => {
			return {
				key: groupCode.cw_groupcode,
				text: `${groupCode.name} (${groupCode.cw_groupcode})`
			}
		})
	})
}

function getGroupCodesAdministrator(): Promise<IDropdownOption[]> {
	return makeCRMApiCall(
		"accounts?$select=name,cw_groupcode&$filter=(Microsoft.Dynamics.CRM.ContainValues(PropertyName='cw_accounttype',PropertyValues=['121640001']) and statecode eq 0 and cw_groupcode ne null)&$orderby=name asc"
	).then(results => {
		return results.value.map((account: any) => {
			return {
				key: account.cw_groupcode,
				text: `${account.name} (${account.cw_groupcode})`
			}
		})
	})
}

function getTreatyYearsBroker(): Promise<IDropdownOption[]> {
	return new Promise(resolve => {
		const treatyYears = getTreatyYears()
		resolve(treatyYears.map(ty => ({ key: ty.treatyYearId, text: `${ty.treatyYearId}: Starting ${ty.startDate.getMonth() + 1}/1/${ty.startDate.getFullYear()}` })))
	})
}

functionMap.getGroupCodesBroker = getGroupCodesBroker
functionMap.getGroupCodesAdministrator = getGroupCodesAdministrator
functionMap.getTreatyYearsBroker = getTreatyYearsBroker

function executeFunction(name: string, ...args: any[]): any {
	const targetFunction = functionMap[name]
	if (!targetFunction) {
		throw `Function not found: ${name}`
	}
	return targetFunction(...args)
}

interface IParameterProps {
	parameter: IReportParameter
	setSelections: (selectedOptions: any) => void
}

export function Parameter(props: IParameterProps): JSX.Element {
	const [options, setOptions] = useState<IDropdownOption[]>([])
	const [selectedOptions, setSelectedOptions] = useState<(string | number)[]>(undefined)
	const [error, setError] = useState<string>()
	const { parameter, setSelections } = props

	useEffect(() => {
		setError(undefined)
		try {
			executeFunction(parameter.Function).then(results => {
				setOptions(results)
			})
		} catch (err) {
			setError(err)
		}
	}, [])

	useEffect(() => {
		setSelections(selectedOptions)
	}, [selectedOptions])

	if (error) {
		return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
	}
	if (!error && parameter.Type === "Dropdown") {
		return (
			<Dropdown
				label={parameter.Label}
				multiSelect={parameter.AllowMultiple}
				options={options}
				onChange={(_ev, option) => {
					if (parameter.AllowMultiple) {
						if (option.selected) {
							setSelectedOptions([...(selectedOptions || []), option.key])
						} else {
							setSelectedOptions((selectedOptions || []).filter(key => key !== option.key))
						}
					} else {
						setSelectedOptions([option.key])
					}
				}}
				onRenderTitle={(options, defaultRender) => {
					if (parameter.RenderKeyInTitle) {
						return (
							<div>
								<span>{options.map(o => o.key).join(", ")}</span>
							</div>
						)
					} else {
						return defaultRender(options)
					}
				}}
			/>
		)
	}

	return <>Unknown paramter type {parameter.Type}</>
}

export function ParametersProvider({ parameters, children }: PropsWithChildren<ParametersProviderProps>) {
	const [results, setResults] = useState<any>({})
	const [allParametersComplete, setAllParametersComplete] = useState<boolean>(false)

	const updateResult = (key: string, result: ParamterResult) => {
		setResults({ ...results, [key]: result })
	}

	useEffect(() => {
		setAllParametersComplete(Object.keys(parameters).every(key => results[key] !== undefined && results[key].length > 0))
	}, [results])

	return (
		<ParametersContext.Provider
			value={{
				AllParametersComplete: allParametersComplete,
				Results: results,
				ParameterElements: Object.keys(parameters).map(key => (
					<Parameter
						key={key}
						parameter={{ ...parameters[key] }}
						setSelections={selectedOptions => {
							updateResult(key, selectedOptions)
						}}
					/>
				))
			}}>
			{children}
		</ParametersContext.Provider>
	)
}
